import type { AccountStatus, ContactStatus } from '@central/contracts';

/**
 * Endereços e vocabulário das telas de cliente.
 *
 * Mora separado das telas pelo mesmo motivo de `app/nav.ts`: o roteador também
 * precisa dos caminhos, e duas listas de rota divergem no primeiro ajuste — o
 * sintoma é um item de menu que leva a 404.
 */

export const CAMINHO_LISTA = '/clientes';

/** O parâmetro do TanStack. O caminho montado fica em `enderecoDoCliente`. */
export const CAMINHO_DETALHE = '/clientes/$clienteId';

export const enderecoDoCliente = (id: string): string => `${CAMINHO_LISTA}/${id}`;

/**
 * As quatro situações comerciais de uma conta.
 *
 * `tom` decide a cor da etiqueta, e o texto explica o que a situação faz — não
 * o que ela é. "Suspenso" e "Encerrado" derrubam as sessões do portal, e quem
 * escolhe no seletor precisa saber disso antes de confirmar, não depois.
 */
export const SITUACOES_DE_CONTA: ReadonlyArray<{
  valor: AccountStatus;
  rotulo: string;
  tom: 'neutro' | 'sucesso' | 'alerta' | 'perigo';
  efeito: string;
}> = [
  {
    valor: 'PROSPECT',
    rotulo: 'Prospecto',
    tom: 'neutro',
    efeito: 'Ainda não é cliente. O cadastro existe para a proposta; o portal fica fechado.',
  },
  {
    valor: 'ACTIVE',
    rotulo: 'Ativo',
    tom: 'sucesso',
    efeito: 'Cliente em atendimento normal.',
  },
  {
    valor: 'SUSPENDED',
    rotulo: 'Suspenso',
    tom: 'alerta',
    efeito:
      'Atendimento parado — inadimplência, contrato em revisão. Derruba as sessões abertas no portal.',
  },
  {
    valor: 'CHURNED',
    rotulo: 'Encerrado',
    tom: 'perigo',
    efeito:
      'Relação encerrada. Derruba as sessões do portal e tira o cliente dos seletores. Nada é apagado: os chamados continuam no histórico.',
  },
] as const;

export const situacaoDeConta = (valor: AccountStatus) =>
  SITUACOES_DE_CONTA.find((s) => s.valor === valor) ?? SITUACOES_DE_CONTA[1]!;

/**
 * As quatro situações de um contato.
 *
 * `INVITED` é o estado em que o contato nasce, e o texto diz por quê: ele
 * ainda não tem senha. É também onde o canal de e-mail deposita quem escreveu
 * para o service desk antes de existir cadastro.
 */
export const SITUACOES_DE_CONTATO: ReadonlyArray<{
  valor: ContactStatus;
  rotulo: string;
  tom: 'neutro' | 'sucesso' | 'alerta' | 'perigo';
  efeito: string;
}> = [
  {
    valor: 'INVITED',
    rotulo: 'Convidado',
    tom: 'neutro',
    efeito: 'Cadastrado e sem senha. Aparece como solicitante nos chamados, mas ainda não entra no portal.',
  },
  { valor: 'ACTIVE', rotulo: 'Ativo', tom: 'sucesso', efeito: 'Entra no portal normalmente.' },
  {
    valor: 'SUSPENDED',
    rotulo: 'Suspenso',
    tom: 'alerta',
    efeito: 'Acesso pausado, sem desligar. Derruba as sessões abertas.',
  },
  {
    valor: 'DEACTIVATED',
    rotulo: 'Desligado',
    tom: 'perigo',
    efeito:
      'Saiu do cliente. Derruba as sessões e some do portal — mas o cadastro fica: os chamados que ele abriu continuam sendo dele.',
  },
] as const;

export const situacaoDeContato = (valor: ContactStatus) =>
  SITUACOES_DE_CONTATO.find((s) => s.valor === valor) ?? SITUACOES_DE_CONTATO[0]!;

export const MODOS_DE_AUTOCADASTRO = [
  ['DISABLED', 'Desligado — só por convite'],
  ['DOMAIN_MATCH', 'Automático pelo domínio de e-mail'],
  ['APPROVAL_REQUIRED', 'Pede aprovação de um contato do cliente'],
] as const;

export const POLITICAS_FORA_DE_VIGENCIA = [
  ['RECORD_AND_WARN', 'Registra e avisa'],
  ['RECORD_AND_BLOCK_SLA', 'Registra sem compromisso de prazo'],
  ['REQUIRE_APPROVAL', 'Registra e pede aprovação'],
] as const;
